const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const MAX_VALVE_SCHEDULES = 20;

export const ISO_WEEKDAYS = [
  { value: 1, short: 'Lun', label: 'Lunes' },
  { value: 2, short: 'Mar', label: 'Martes' },
  { value: 3, short: 'Mié', label: 'Miércoles' },
  { value: 4, short: 'Jue', label: 'Jueves' },
  { value: 5, short: 'Vie', label: 'Viernes' },
  { value: 6, short: 'Sáb', label: 'Sábado' },
  { value: 7, short: 'Dom', label: 'Domingo' },
];

const DAY_NAME_TO_ISO = {
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
  SUN: 7,
};

let fallbackId = 0;

export function createScheduleId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  fallbackId += 1;
  return `schedule-${Date.now().toString(36)}-${fallbackId.toString(36)}`;
}

export function isValidTimeZone(timeZone) {
  if (!timeZone || typeof timeZone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('es-MX', { timeZone }).format();
    return true;
  } catch {
    return false;
  }
}

export function browserTimeZone() {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidTimeZone(resolved) ? resolved : 'UTC';
}

function normalizeDays(rawDays) {
  if (!Array.isArray(rawDays)) return [];
  return [...new Set(rawDays.map((day) => {
    if (typeof day === 'string' && DAY_NAME_TO_ISO[day.trim().toUpperCase()]) {
      return DAY_NAME_TO_ISO[day.trim().toUpperCase()];
    }
    return Number(day);
  }).filter((day) => Number.isInteger(day) && day >= 1 && day <= 7))].sort((a, b) => a - b);
}

/**
 * Reduce cualquier representación procedente del backend a un contrato único.
 * `enabled` sólo se activa con el booleano explícito true: una configuración
 * incompleta nunca debe convertirse accidentalmente en una automatización.
 */
export function normalizeValveSchedule(raw = {}, fallbackTimeZone = browserTimeZone()) {
  const requestedTimeZone = raw.timezone ?? raw.time_zone ?? fallbackTimeZone;
  const requestedTimeZoneIsValid = isValidTimeZone(requestedTimeZone);
  const timeZone = requestedTimeZoneIsValid
    ? requestedTimeZone
    : (isValidTimeZone(fallbackTimeZone) ? fallbackTimeZone : 'UTC');

  return {
    id: String(raw.id ?? raw.schedule_id ?? '').trim(),
    name: String(raw.name ?? '').trim(),
    // Una zona corrupta cambia el instante real de ejecución. Se recupera a
    // la zona local, pero exige que el usuario vuelva a activar el horario.
    enabled: raw.enabled === true && requestedTimeZoneIsValid,
    timezone: timeZone,
    days: normalizeDays(raw.days ?? raw.weekdays),
    open_time: String(raw.open_time ?? raw.openTime ?? '').trim(),
    close_time: String(raw.close_time ?? raw.closeTime ?? '').trim(),
  };
}

export function normalizeValveSchedules(raw, fallbackTimeZone = browserTimeZone()) {
  if (typeof raw === 'string') {
    try {
      return normalizeValveSchedules(JSON.parse(raw), fallbackTimeZone);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw.filter((item) => item && typeof item === 'object').map((item, index) => {
    const normalized = normalizeValveSchedule(item, fallbackTimeZone);
    return normalized.id ? normalized : { ...normalized, id: `legacy-schedule-${index + 1}` };
  });
}

export function validateValveSchedule(raw) {
  const schedule = normalizeValveSchedule(raw, raw?.timezone || 'UTC');
  const errors = {};

  if (!schedule.id) errors.id = 'La programación no tiene identificador.';
  if (schedule.name.length > 80) errors.name = 'El nombre puede tener hasta 80 caracteres.';
  if (schedule.days.length === 0) errors.days = 'Selecciona al menos un día.';
  if (!schedule.open_time && !schedule.close_time) {
    errors.times = 'Configura al menos una hora de apertura o de cierre.';
  }
  if (schedule.open_time && !TIME_PATTERN.test(schedule.open_time)) {
    errors.open_time = 'Selecciona una hora de apertura válida.';
  }
  if (schedule.close_time && !TIME_PATTERN.test(schedule.close_time)) {
    errors.close_time = 'Selecciona una hora de cierre válida.';
  }
  if (!isValidTimeZone(schedule.timezone)) errors.timezone = 'La zona horaria no es válida.';

  return { valid: Object.keys(errors).length === 0, errors, schedule };
}

/**
 * Cuando hay un ciclo completo, `days` identifica el día en que se cierra.
 * Una apertura igual o anterior al cierre pertenece al día siguiente. Con una
 * sola acción no hay ciclo y `days` se aplica directamente a esa acción.
 */
export function opensOnFollowingDay(raw) {
  const schedule = normalizeValveSchedule(raw, raw?.timezone || 'UTC');
  return Boolean(
    schedule.open_time
    && schedule.close_time
    && schedule.open_time <= schedule.close_time
  );
}

/** Detecta tanto órdenes opuestas como ejecuciones duplicadas en el mismo minuto. */
export function findScheduleConflicts(rawSchedules = []) {
  const occupied = new Map();
  const conflicts = [];

  rawSchedules.forEach((raw) => {
    const { valid, schedule } = validateValveSchedule(raw);
    if (!valid || !schedule.enabled) return;

    schedule.days.forEach((anchorDay) => {
      const openDay = opensOnFollowingDay(schedule) ? (anchorDay % 7) + 1 : anchorDay;
      [
        schedule.open_time ? { action: 'open', time: schedule.open_time, day: openDay } : null,
        schedule.close_time ? { action: 'close', time: schedule.close_time, day: anchorDay } : null,
      ].filter(Boolean).forEach((event) => {
        const key = `${schedule.timezone}|${event.day}|${event.time}`;
        const previous = occupied.get(key);
        if (previous) {
          conflicts.push({ ...event, timezone: schedule.timezone, first: previous, second: { schedule, ...event } });
        } else {
          occupied.set(key, { schedule, ...event });
        }
      });
    });
  });

  return conflicts;
}

export function formatScheduleDays(days = []) {
  const normalized = normalizeDays(days);
  if (normalized.length === 7) return 'Todos los días';
  if (normalized.join(',') === '1,2,3,4,5') return 'Lunes a viernes';
  if (normalized.join(',') === '6,7') return 'Fin de semana';
  return normalized
    .map((day) => ISO_WEEKDAYS.find((item) => item.value === day)?.short)
    .filter(Boolean)
    .join(', ');
}

function zonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce((result, part) => {
    if (part.type !== 'literal') result[part.type] = Number(part.value);
    return result;
  }, {});
  return parts;
}

/* Convierte una hora civil IANA a UTC sin depender de una biblioteca externa.
   La verificación final descarta minutos inexistentes durante un salto DST. */
function zonedDateTimeToUtc({ year, month, day, hour, minute }, timeZone) {
  const desired = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  let candidate = desired;

  for (let i = 0; i < 4; i += 1) {
    const seen = zonedParts(new Date(candidate), timeZone);
    const represented = Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute, seen.second || 0, 0);
    const correction = desired - represented;
    if (correction === 0) break;
    candidate += correction;
  }

  const check = zonedParts(new Date(candidate), timeZone);
  if (
    check.year !== year || check.month !== month || check.day !== day
    || check.hour !== hour || check.minute !== minute
  ) return null;
  return new Date(candidate);
}

function isoWeekday(year, month, day) {
  const jsDay = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return jsDay === 0 ? 7 : jsDay;
}

/** Devuelve la próxima orden válida; no "reproduce" eventos que ya pasaron. */
export function nextScheduledEvent(rawSchedules = [], now = new Date()) {
  let next = null;

  rawSchedules.forEach((raw) => {
    const { valid, schedule } = validateValveSchedule(raw);
    if (!valid || !schedule.enabled) return;

    const today = zonedParts(now, schedule.timezone);
    // offset -1 captura la apertura de hoy que pertenece al cierre de ayer.
    for (let offset = -1; offset <= 7; offset += 1) {
      const anchorDay = new Date(Date.UTC(today.year, today.month - 1, today.day + offset));
      const anchorYear = anchorDay.getUTCFullYear();
      const anchorMonth = anchorDay.getUTCMonth() + 1;
      const anchorDate = anchorDay.getUTCDate();
      if (!schedule.days.includes(isoWeekday(anchorYear, anchorMonth, anchorDate))) continue;

      const openDay = new Date(anchorDay.getTime());
      if (opensOnFollowingDay(schedule)) openDay.setUTCDate(openDay.getUTCDate() + 1);
      const events = [
        schedule.open_time ? {
          action: 'open',
          time: schedule.open_time,
          year: openDay.getUTCFullYear(),
          month: openDay.getUTCMonth() + 1,
          day: openDay.getUTCDate(),
        } : null,
        schedule.close_time ? {
          action: 'close',
          time: schedule.close_time,
          year: anchorYear,
          month: anchorMonth,
          day: anchorDate,
        } : null,
      ].filter(Boolean);

      for (const event of events) {
        const [hour, minute] = event.time.split(':').map(Number);
        const at = zonedDateTimeToUtc({
          year: event.year,
          month: event.month,
          day: event.day,
          hour,
          minute,
        }, schedule.timezone);
        if (!at || at.getTime() <= now.getTime()) continue;
        if (!next || at.getTime() < next.at.getTime()) {
          next = { ...event, at, timezone: schedule.timezone, schedule };
        }
      }
    }
  });

  return next;
}

export function formatNextScheduledEvent(event) {
  if (!event) return 'No hay ejecuciones programadas.';
  const action = event.action === 'open' ? 'Abrir' : 'Cerrar';
  const when = new Intl.DateTimeFormat('es-MX', {
    timeZone: event.timezone,
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  }).format(event.at);
  return `${action} · ${when}`;
}
